/* ============================================================
   PICHEA — Estación de Café para Eventos · interacciones
   Vanilla JS · CSS-first motion (Emil Kowalski principles)
   Sin dependencias. Respeta prefers-reduced-motion.
   Consciente del editor de Shopify (re-init en shopify:section:load).
   ============================================================ */
(function () {
  "use strict";

  var reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function $all(sel, root) { return (root || document).querySelectorAll(sel); }

  /* ---------- 1) Reveal on scroll (IntersectionObserver) ---------- */
  function revealNow(root) {
    $all(".pcx-reveal, .pcx-stagger", root).forEach(function (el) {
      if (el.classList.contains("pcx-stagger")) {
        Array.prototype.forEach.call(el.children, function (c, i) {
          c.style.setProperty("--i", i);
        });
      }
      el.classList.add("is-in");
    });
  }
  function initReveal(root) {
    var els = $all(".pcx-reveal, .pcx-stagger", root);
    // En el editor (o sin IO / reduced-motion) mostramos de inmediato:
    // el observer es poco fiable dentro del iframe del editor.
    if (!("IntersectionObserver" in window) || reduce || designMode) {
      revealNow(root);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          if (e.target.classList.contains("pcx-stagger")) {
            Array.prototype.forEach.call(e.target.children, function (c, i) {
              c.style.setProperty("--i", i);
            });
          }
          e.target.classList.add("is-in");
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.16, rootMargin: "0px 0px -8% 0px" });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---------- 2) Contadores animados (rAF) ---------- */
  function initCounters(root) {
    var nums = $all("[data-count-to]", root);
    if (!nums.length) return;
    if (reduce) {
      nums.forEach(function (n) { n.textContent = formatCount(n); });
      return;
    }
    // En el editor animamos de inmediato (la sección ya está a la vista).
    if (!("IntersectionObserver" in window) || designMode) {
      nums.forEach(function (n) { animateCount(n); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        animateCount(e.target);
        io.unobserve(e.target);
      });
    }, { threshold: 0.5 });
    nums.forEach(function (n) { io.observe(n); });
  }
  function formatCount(n) {
    var to = parseFloat(n.getAttribute("data-count-to")) || 0;
    var dec = parseInt(n.getAttribute("data-count-decimals") || "0", 10);
    return (n.getAttribute("data-count-prefix") || "") + to.toFixed(dec) + (n.getAttribute("data-count-suffix") || "");
  }
  function animateCount(n) {
    if (n.dataset.counting === "1") return; // evita doble animación simultánea
    n.dataset.counting = "1";
    var to = parseFloat(n.getAttribute("data-count-to")) || 0;
    var dec = parseInt(n.getAttribute("data-count-decimals") || "0", 10);
    var pre = n.getAttribute("data-count-prefix") || "";
    var suf = n.getAttribute("data-count-suffix") || "";
    var dur = 1100, start = null;
    function ease(t) { return 1 - Math.pow(1 - t, 3); } // ease-out cubic
    function step(ts) {
      if (start === null) start = ts;
      var p = Math.min((ts - start) / dur, 1);
      n.textContent = pre + (to * ease(p)).toFixed(dec) + suf;
      if (p < 1) { requestAnimationFrame(step); }
      else { n.dataset.counting = "0"; }
    }
    requestAnimationFrame(step);
  }

  /* ---------- 3) Barra CTA sticky (aparece tras el hero) ---------- */
  function initStickyCTA() {
    var bar = document.querySelector("[data-sticky-cta]");
    var hero = document.querySelector("[data-hero]");
    if (!bar || !hero || !("IntersectionObserver" in window)) return;
    if (bar.dataset.stickyReady === "1") return;
    bar.dataset.stickyReady = "1";
    var io = new IntersectionObserver(function (entries) {
      bar.classList.toggle("is-visible", !entries[0].isIntersecting);
    }, { threshold: 0, rootMargin: "-120px 0px 0px 0px" });
    io.observe(hero);
  }

  /* ---------- 4) Carrusel de testimonios (scroll-snap + flechas) ---------- */
  function initCarousels(root) {
    $all("[data-carousel]", root).forEach(function (rootEl) {
      if (rootEl.dataset.carouselReady === "1") return;
      rootEl.dataset.carouselReady = "1";
      var track = rootEl.querySelector("[data-carousel-track]");
      var prev = rootEl.querySelector("[data-carousel-prev]");
      var next = rootEl.querySelector("[data-carousel-next]");
      if (!track) return;
      function page(dir) {
        var slide = track.querySelector(":scope > *");
        var w = slide ? slide.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
        track.scrollBy({ left: dir * w, behavior: reduce ? "auto" : "smooth" });
      }
      if (prev) prev.addEventListener("click", function () { page(-1); });
      if (next) next.addEventListener("click", function () { page(1); });
    });
  }

  /* ---------- 5) Marquee infinito de logos (duplica el track) ---------- */
  function initMarquee(root) {
    $all("[data-marquee]", root).forEach(function (track) {
      if (reduce) return;
      if (track.dataset.marqueeReady === "1") return; // no duplicar el clon
      track.dataset.marqueeReady = "1";
      var clone = track.cloneNode(true);
      clone.setAttribute("aria-hidden", "true");
      clone.removeAttribute("data-marquee"); // el clon no debe re-clonarse
      track.parentNode.appendChild(clone);
    });
  }

  /* ---------- 6) FAQ acordeón (transición de altura suave) ---------- */
  function initAccordion(root) {
    $all("[data-accordion] [data-acc-item]", root).forEach(function (item) {
      if (item.dataset.accReady === "1") return;
      item.dataset.accReady = "1";
      var btn = item.querySelector("[data-acc-trigger]");
      var panel = item.querySelector("[data-acc-panel]");
      if (!btn || !panel) return;
      btn.setAttribute("aria-expanded", "false");
      panel.style.height = "0px";
      btn.addEventListener("click", function () {
        var open = btn.getAttribute("aria-expanded") === "true";
        item.closest("[data-accordion]").querySelectorAll("[data-acc-item]").forEach(function (other) {
          if (other !== item) closePanel(other);
        });
        if (open) { closePanel(item); }
        else {
          btn.setAttribute("aria-expanded", "true");
          item.classList.add("is-open");
          panel.style.height = panel.scrollHeight + "px";
        }
      });
      panel.addEventListener("transitionend", function () {
        if (btn.getAttribute("aria-expanded") === "true") panel.style.height = "auto";
      });
    });
    function closePanel(item) {
      var btn = item.querySelector("[data-acc-trigger]");
      var panel = item.querySelector("[data-acc-panel]");
      panel.style.height = panel.scrollHeight + "px";
      requestAnimationFrame(function () {
        panel.style.height = "0px";
        btn.setAttribute("aria-expanded", "false");
        item.classList.remove("is-open");
      });
    }
  }

  /* ---------- 7) Lightbox de galería (singleton de página) ---------- */
  function initLightbox() {
    var gallery = document.querySelector("[data-gallery]");
    if (!gallery) return;
    if (gallery.dataset.lightboxReady === "1") return;
    gallery.dataset.lightboxReady = "1";
    var box = document.querySelector(".pcx-lightbox");
    if (!box) {
      box = document.createElement("div");
      box.className = "pcx-lightbox";
      box.setAttribute("role", "dialog");
      box.setAttribute("aria-modal", "true");
      box.innerHTML = '<button class="pcx-lightbox__close" aria-label="Cerrar">&times;</button><img alt="">';
      document.body.appendChild(box);
    }
    var img = box.querySelector("img");
    function open(src, alt) {
      img.src = src; img.alt = alt || "";
      box.classList.add("is-open");
      document.documentElement.style.overflow = "hidden";
    }
    function close() {
      box.classList.remove("is-open");
      document.documentElement.style.overflow = "";
    }
    gallery.addEventListener("click", function (e) {
      var t = e.target.closest("[data-gallery-item] img");
      if (t) open(t.getAttribute("data-full") || t.src, t.alt);
    });
    box.addEventListener("click", function (e) {
      if (e.target === box || e.target.closest(".pcx-lightbox__close")) close();
    });
    document.addEventListener("keydown", function (e) { if (e.key === "Escape") close(); });
  }

  /* ---------- 8b) Video facade — carga el embed al hacer click ---------- */
  function initVideo(root) {
    $all("[data-video-frame]", root).forEach(function (frame) {
      var embed = frame.getAttribute("data-embed");
      if (!embed) return; // <video> nativo: el navegador maneja play
      frame.addEventListener("click", function () {
        var iframe = document.createElement("iframe");
        iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture; fullscreen");
        iframe.setAttribute("allowfullscreen", "");
        iframe.setAttribute("title", frame.getAttribute("data-title") || "Video");
        iframe.src = embed;
        frame.innerHTML = "";
        frame.appendChild(iframe);
        frame.style.cursor = "default";
      }, { once: true });
    });
  }

  /* ---------- 8) Smooth scroll a anchors internos ---------- */
  function initAnchors(root) {
    $all('.pcx a[href^="#"]', root).forEach(function (a) {
      if (a.dataset.anchorReady === "1") return;
      a.dataset.anchorReady = "1";
      a.addEventListener("click", function (e) {
        var id = a.getAttribute("href");
        if (id.length < 2) return;
        var target = document.querySelector(id);
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
      });
    });
  }

  /* ---------- init ---------- */
  // Página completa (carga inicial / storefront real)
  function initPage() {
    var d = document;
    initReveal(d); initCounters(d); initStickyCTA(); initCarousels(d);
    initMarquee(d); initAccordion(d); initLightbox(); initVideo(d); initAnchors(d);
  }
  // Una sola sección re-renderizada por el editor de Shopify
  function initSection(root) {
    if (!root) return;
    revealNow(root);          // mostrar de inmediato (sin esperar scroll)
    initCounters(root);       // re-animar contadores de esa sección
    initStickyCTA();
    initCarousels(root);
    initMarquee(root);
    initAccordion(root);
    initLightbox();
    initVideo(root);
    initAnchors(root);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initPage);
  } else {
    initPage();
  }

  // Soporte del editor de Shopify: re-inicializar al renderizar/seleccionar secciones.
  if (designMode) {
    document.addEventListener("shopify:section:load", function (e) { initSection(e.target); });
    document.addEventListener("shopify:section:select", function (e) { revealNow(e.target); });
    document.addEventListener("shopify:block:select", function (e) {
      revealNow(e.target.closest(".shopify-section") || e.target);
    });
  }
})();
